import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AudioMonitor from 'utils/AudioMonitor';
import { makeStyles } from '@material-ui/core';
import { colors } from '@lifesize/ux-assets';

const micLevelLightCount = 20;

export interface MicLevelProps {
  microphoneDeviceId?: string;
  microphoneEnabled: boolean;
}

const MicrophoneLevel = (props: MicLevelProps) => {
  const audioMonitorRef = React.useRef<AudioMonitor>();
  const { microphoneDeviceId, microphoneEnabled } = props;
  const classes = useStyles({});
  const [microphoneLevel, setMicrophoneLevel] = useState(0);

  /** Handler to update mic level state */
  const onAudioMonitorUpdate = useCallback(({ micLevel }) => {
    const newMicLevel = Number((micLevelLightCount * micLevel).toFixed(0));
    if (micLevel !== newMicLevel) {
      setMicrophoneLevel(newMicLevel);
    }
  }, []);

  /** To create new audio monitor instance if needed */
  useEffect(() => {
    if (!audioMonitorRef.current) {
      audioMonitorRef.current = new AudioMonitor(
        (value) => onAudioMonitorUpdate(value),
        () => null
      );
    }
  }, [onAudioMonitorUpdate, microphoneEnabled]);

  /** Handler to connect audio monitor on new device */
  const connectAudioMonitor = useCallback(() => {
    if (!!audioMonitorRef && audioMonitorRef.current) {
      if (!!microphoneDeviceId && microphoneEnabled) {
        audioMonitorRef?.current?.connect(microphoneDeviceId);
      } else {
        audioMonitorRef?.current?.disconnect();
      }
    }
  }, [microphoneDeviceId, microphoneEnabled]);

  /** To connect audio monitor */
  useEffect(() => {
    if (!!audioMonitorRef.current) {
      connectAudioMonitor();
    }
  }, [audioMonitorRef, connectAudioMonitor]);

  /** To disconnect and destroy audio monitor */
  useEffect(() => {
    return function cleanup() {
      if (audioMonitorRef.current) {
        audioMonitorRef.current.destroy();
      }
    };
  }, []);

  const micLevelLights = useCallback(() => {
    const icons = new Array(micLevelLightCount);
    icons.fill(classes.off);
    if (!!microphoneDeviceId && microphoneEnabled) {
      icons.fill(classes.on, 0, microphoneLevel);
    }
    return icons;
  }, [microphoneLevel, classes.on, classes.off, microphoneDeviceId, microphoneEnabled]);

  return (
    <>
      <div className={classes.levelLabel}>
        <FormattedMessage id="components.avSettingsModal.levelLabel" defaultMessage="Microphone level" />
      </div>
      <div className={classes.audioBar}>
        {micLevelLights().map((lightState, index) => {
          const classList = [classes.audioSquare, lightState].join(' ');
          return <i key={index} className={classList} data-testid="audio-level-square" />;
        })}
      </div>
    </>
  );
};

const useStyles = makeStyles(() => ({
  levelLabel: {
    color: colors.sentinel,
    fontSize: '0.857rem',
    lineHeight: '20px'
  },
  audioBar: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  audioSquare: {
    borderRadius: '4px',
    height: '12px',
    width: '12px'
  },
  on: { backgroundColor: colors.agileGreen },
  off: { backgroundColor: colors.missionFog }
}));

export default memo(MicrophoneLevel);
